import React, { ReactNode, useCallback, useEffect, useState } from 'react';

// Components
import { VirtualizedTable } from '../_ui/virtualized-table.component';
import { IColumnMap } from './ImportColumnMatcher/import-matcher.interface';
import { IImportColumn, ImportCell, ImportRow } from './importer.interface';
import { orderColumnIndicesByRequiredFirst } from './importer.helpers';
import { ColumnProps } from 'react-virtualized';
import { Box } from '@material-ui/core';
import _ from 'lodash';
import { Spacer } from '../_ui/structure.components';
import { PrimaryButton } from '../_ui/buttons.component';
import Typography from '@material-ui/core/Typography';

interface IImporterResults {
  rows: ImportRow[];
  updateRowValue?: Function;
  columnMap: IColumnMap[];
  requiredColumns: IImportColumn[];
  headerRowIndex: number;
  canEdit?: boolean;
  onUpdateRows: Function;
  completeReview: Function;
  reviewSummary?: ReactNode;
}

interface IRowGetter {
  index: number;
}

interface IColumnPropsWithOrigData extends ColumnProps {
  originalIndex: number;
}

export const ImporterResults: React.FC<IImporterResults> = ({
  rows,
  // updateRowValue,
  columnMap,
  requiredColumns,
  headerRowIndex,
  canEdit,
  onUpdateRows,
  completeReview,
  reviewSummary
}) => {
  const [columns, setColumns] = useState<IColumnPropsWithOrigData[]>([]);
  const [rowHashes, setRowHashes] = useState<any[]>([]);
  const [countInvalid, setCountInvalid] = useState(0);
  const [invalidRowMap, setInvalidRowMap] = useState<any>({});

  const requiredValidator = (value: ImportCell) => !!value

  useEffect(() => {
    const nextInvalidRowMap: { [key: number]: boolean } = {};
    const nextColumns: IColumnPropsWithOrigData[] = [];
    const requiredKeys = new Set(requiredColumns.map(({ key }) => key));
    let nextCountInvalid = 0;

    orderColumnIndicesByRequiredFirst(columnMap, requiredColumns).forEach(index => {
      const map = columnMap[index];

      if (map?.column && !map.isIgnored) {
        nextColumns.push({
          width: 200,
          label: map.column.label,
          dataKey: `cell_${index}`,
          originalIndex: index
        });
      }
    });

    const rowHashes: any[] = [];
    rows.forEach((row, rowIndex) => {
      const rowHash: any = {};

      if (rowIndex !== headerRowIndex) {
        row.forEach((cell, index) => {
          if (columnMap[index] && columnMap[index].column && !columnMap[index].isIgnored) {
            const column = columnMap[index].column;
            const validators = column?.validators;
            // Derived, never stored: the user can re-map a column after the header was matched.
            let isValid = column && requiredKeys.has(column.key) ? requiredValidator(cell) : true;

            for (let i = 0; isValid && validators && i < validators.length; i++) {
              isValid = validators[i](cell, column?.format);
            }

            rowHash[`cell_${index}`] = { cell, isValid };

            if (!isValid) {
              nextCountInvalid += 1;
              nextInvalidRowMap[rowIndex] = true;
            }
          }
        });

        rowHashes.push(rowHash);
      }
    });

    setCountInvalid(nextCountInvalid);
    setRowHashes(rowHashes);
    setColumns(nextColumns);
    setInvalidRowMap(nextInvalidRowMap);
  }, [columnMap, requiredColumns, rows, headerRowIndex]);

  const onCellEdit = useCallback(_.debounce((dataKey: any, rowIndex: number, value: string) => {
    const editRowIndex = _.isNumber(headerRowIndex) ? rowIndex + 1 : rowIndex;
    const columnIndex = columns.find((c) => c.dataKey === dataKey)?.originalIndex

    if (canEdit && columnIndex !== undefined) {
      const nextRows = [...rows];
      nextRows[editRowIndex] = [...nextRows[editRowIndex]]
      nextRows[editRowIndex][columnIndex] = value;

      onUpdateRows(nextRows);
    }
  }, 100), [columns, rows]);

  const buttons = (
    <Box display="flex" m={2} alignItems="center">
      {countInvalid > 0 && (
        <Box>
          <Typography gutterBottom>{countInvalid} invalid cells. Rows with invalid cells will not be imported.</Typography>
          <Typography>Click on a cell to edit it and fix the error.</Typography>
        </Box>
      )}
      <Spacer />
      <PrimaryButton onClick={() => completeReview(invalidRowMap)}>Confirm &amp; Import</PrimaryButton>
    </Box>
  );

  return (
    <Box>
      {buttons}

      {reviewSummary}

      <Box width="100%" height="400px" style={{ background: '#fff' }}>
        <VirtualizedTable
          rowCount={rowHashes.length}
          rowGetter={({ index }: IRowGetter) => rowHashes[index]}
          columns={columns}
          onCellEdit={onCellEdit}
        />
      </Box>
      {buttons}
    </Box>
  );
};
